import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Contact, Lead } from '../../../types';
import { cleanPhone, comparePhones, contactPhoneKeyVariants } from '../../../utils/phoneUtils';
import { ChatConversation } from './useChat';
import { mergeLeadsById, mapOpportunityRow, OPPORTUNITY_SELECT } from './contactPanelDeals.helpers';

interface UseContactPanelDealLeadsParams {
    conversation: ChatConversation | undefined;
    organizationId?: string;
    currentContact: Contact | null;
    leads: Lead[];
}

export function useContactPanelDealLeads({
    conversation,
    organizationId,
    currentContact,
    leads
}: UseContactPanelDealLeadsParams) {
    const [currentLeads, setCurrentLeads] = useState<Lead[]>([]);

    const fallbackLeadsFromContext = useMemo(() => {
        if (!conversation) return [];
        const phone = cleanPhone(conversation.id);
        return leads
            .filter(lead =>
                comparePhones(lead.whatsApp || '', phone) &&
                lead.status !== 'won' &&
                lead.status !== 'lost'
            )
            .sort((a, b) => {
                const dateA = new Date(a.entryDate || a.date || 0).getTime();
                const dateB = new Date(b.entryDate || b.date || 0).getTime();
                return dateB - dateA;
            });
    }, [conversation, leads]);

    const fetchContactIds = useCallback(async (phone: string): Promise<string[]> => {
        if (!conversation || !organizationId) return [];

        const contactIds = new Set<string>();
        if (conversation.contactId) contactIds.add(conversation.contactId);
        if (currentContact?.id) contactIds.add(currentContact.id);

        const phoneVariants = contactPhoneKeyVariants(phone);
        if (phoneVariants.length > 0) {
            const { data } = await supabase
                .from('contacts')
                .select('id')
                .eq('organization_id', organizationId)
                .in('phone_key', phoneVariants);

            for (const row of data || []) contactIds.add(row.id);
        }

        const searchTerm = phone.length >= 10 ? phone.slice(-10) : phone;
        const { data } = await supabase
            .from('contacts')
            .select('id')
            .eq('organization_id', organizationId)
            .or(`phone.ilike.%${searchTerm}%,whatsapp.ilike.%${searchTerm}%`);

        for (const row of data || []) contactIds.add(row.id);
        return [...contactIds];
    }, [conversation, organizationId, currentContact?.id]);

    const fetchLeadsFromServer = useCallback(async (): Promise<Lead[]> => {
        if (!conversation || !organizationId) return [];

        const phone = cleanPhone(conversation.id);
        const contactIds = await fetchContactIds(phone);
        let query = supabase
            .from('opportunities')
            .select(OPPORTUNITY_SELECT)
            .eq('organization_id', organizationId)
            .is('won_at', null)
            .is('lost_at', null)
            .order('created_at', { ascending: false });

        if (contactIds.length > 0) {
            query = query.in('contact_id', contactIds);
        } else {
            query = query.limit(500);
        }

        const { data, error } = await query;
        if (error) {
            console.error('[useContactPanelDeals] Error fetching leads for panel:', error);
            return [];
        }

        const rows = data || [];
        return (contactIds.length > 0 ? rows : rows.filter((row: any) => {
            const contactPhone = cleanPhone(row?.contact?.whatsapp || row?.contact?.phone || '');
            return comparePhones(contactPhone, phone);
        })).map((row: any) => mapOpportunityRow(row));
    }, [conversation, organizationId, fetchContactIds]);

    const refreshPanelLeads = useCallback(async () => {
        const serverLeads = await fetchLeadsFromServer();
        setCurrentLeads(mergeLeadsById(fallbackLeadsFromContext, serverLeads));
    }, [fallbackLeadsFromContext, fetchLeadsFromServer]);

    useEffect(() => {
        refreshPanelLeads();
    }, [refreshPanelLeads]);

    return { currentLeads, refreshPanelLeads };
}
